import { inject, Injectable } from '@angular/core';

import { ORANGE_APP_INFO_PROPERTIES, OrangeAppInfo } from '../business/orange-app-info.interface';
import { OrangeLocalStorageService } from './orange-local-storage.service';

/**
 * Reads and validates the `orange-app-info` local storage entry, written by the Orange native app before loading the webview
 *
 * The entry is resolved lazily on first read, then locked for the whole session once at least one valid property is found
 * While nothing valid is found, each read retries : the entry can be written after the app bootstrap
 * Note : the local storage entry has no TTL, it lives as long as the webview origin storage
 */
@Injectable({
    providedIn: 'root',
})
export class OrangeAppInfoService {
    private readonly localStorage = inject(OrangeLocalStorageService);

    /**
     * Local storage entry written by the Orange native app
     */
    private readonly APP_INFO_KEY = 'orange-app-info';

    /**
     * Max accepted length for a property read from this external source
     * @type {number}
     */
    private readonly MAX_VALUE_LENGTH: number = 32;

    /**
     * Accepted characters for a property read from this external source : printable ASCII only
     * `;` and `=` are also rejected in `sanitize`, they are reserved as separators in the `x-orange-app-info` header
     * @type {RegExp}
     */
    private readonly VALID_VALUE_PATTERN: RegExp = /^[ -~]+$/;

    /**
     * Validated app info, locked for the whole session once resolved
     */
    private lockedAppInfo: OrangeAppInfo | null = null;

    /**
     * Validated native app info, holding only the valid properties
     * @returns {OrangeAppInfo} `null` when no property is valid
     */
    public get appInfo(): OrangeAppInfo | null {
        return this.resolve();
    }

    /**
     * Native app name (`name` property)
     * @returns {string} `null` when absent or invalid
     */
    public get appName(): string | null {
        return this.resolve()?.name ?? null;
    }

    /**
     * Native app version (`version` property)
     * @returns {string} `null` when absent or invalid
     */
    public get appVersion(): string | null {
        return this.resolve()?.version ?? null;
    }

    /**
     * Read and validate the local storage entry, locking the result once at least one property is valid
     * @returns {OrangeAppInfo} `null` while nothing valid is found
     */
    private resolve(): OrangeAppInfo | null {
        if (this.lockedAppInfo) {
            return this.lockedAppInfo;
        }

        const rawAppInfo = this.localStorage.getJson<OrangeAppInfo>(this.APP_INFO_KEY);
        const appInfo: OrangeAppInfo = {};

        for (const property of ORANGE_APP_INFO_PROPERTIES) {
            const value = this.sanitize(rawAppInfo?.[property]);
            if (value) {
                appInfo[property] = value;
            }
        }

        if (!Object.keys(appInfo).length) {
            return null;
        }

        this.lockedAppInfo = Object.freeze(appInfo);
        return this.lockedAppInfo;
    }

    /**
     * Validate a property value : non empty trimmed string, reasonably sized, accepted characters only
     * @param {unknown} value raw property value
     * @returns {string} trimmed value, `null` when invalid
     */
    private sanitize(value: unknown): string | null {
        if (typeof value !== 'string') {
            return null;
        }

        const trimmedValue: string = value.trim();
        if (!trimmedValue || trimmedValue.length > this.MAX_VALUE_LENGTH) {
            return null;
        }
        if (!this.VALID_VALUE_PATTERN.test(trimmedValue) || trimmedValue.includes(';') || trimmedValue.includes('=')) {
            return null;
        }

        return trimmedValue;
    }
}
